import { LoadingSpinner } from "@kubdee/ui/custom/feedback/loading-spinner";
import { useEffect, useRef } from "react";
import { useAuthStore } from "@kubdee/sdk/stores/auth-store";
import { PlatformWebSocketProvider } from "@/providers/platform-websocket-provider";
import { CommandPalette } from "@kubdee/ui/custom/search/command-palette";

import { getBaseUrl } from "@kubdee/sdk/api-client";

const NO_COMPANY_ACCESS_LOGIN_URL = "/login?error=no_company_access";

function MainLoadingSkeleton() {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <LoadingSpinner size="3xl" className="text-muted-foreground" />
        <p className="text-sm text-muted-foreground">กำลังตรวจสอบสิทธิ์...</p>
      </div>
    </div>
  );
}

export default function MainLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const isLoading = useAuthStore((s) => s.isLoading);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const initialized = useRef(false);

  // Fetch user data once on mount
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const store = useAuthStore.getState();

    async function checkAuth() {
      try {
        store.setLoading(true);
        const res = await fetch(`${getBaseUrl()}/platform/auth/me`, {
          credentials: "include",
        });

        if (!res.ok) {
          // Token expired — try refresh before redirecting
          const refreshRes = await fetch(
            `${getBaseUrl()}/platform/auth/refresh`,
            {
              method: "POST",
              credentials: "include",
            }
          );
          if (refreshRes.ok) {
            // Retry /me with new token
            const retryRes = await fetch(`${getBaseUrl()}/platform/auth/me`, {
              credentials: "include",
            });
            if (retryRes.ok) {
              const retryJson = await retryRes.json();
              const retryData = retryJson.data;
              store.setUser(retryData.user);
              store.setTenant(retryData.tenant ?? null);
              // No tenant — redirect to onboarding
              if (!retryData.tenant?.id) {
                window.location.href = "/onboarding";
                return;
              }
              if (!store.currentCompanyId) {
                try {
                  const compRes = await fetch(
                    `${getBaseUrl()}/core/companies`,
                    { credentials: "include" }
                  );
                  if (compRes.ok) {
                    const compJson = await compRes.json();
                    const companies = compJson.data;
                    if (Array.isArray(companies) && companies.length > 0) {
                      store.setCurrentCompany(companies[0].id);
                    } else {
                      // No companies — redirect to onboarding
                      window.location.href = "/onboarding";
                      return;
                    }
                  } else if (compRes.status === 403) {
                    window.location.href = NO_COMPANY_ACCESS_LOGIN_URL;
                    return;
                  }
                } catch {
                  /* ignore */
                }
              }
              store.setLoading(false);
              return;
            }
          }
          // Refresh also failed — clear cookies via logout then go to login
          store.reset();
          try {
            await fetch(`${getBaseUrl()}/platform/auth/logout`, {
              method: "POST",
              credentials: "include",
            });
          } catch {
            /* ignore — best effort */
          }
          window.location.href = "/login";
          return;
        }

        const json = await res.json();
        const data = json.data;

        store.setUser(data.user);
        store.setTenant(data.tenant ?? null);

        // No tenant — redirect to onboarding (Step 1: create org)
        if (!data.tenant?.id) {
          window.location.href = "/onboarding";
          return;
        }

        // Auto-select first company if none selected
        if (!store.currentCompanyId) {
          try {
            const compRes = await fetch(`${getBaseUrl()}/core/companies`, {
              credentials: "include",
            });
            if (compRes.ok) {
              const compJson = await compRes.json();
              const companies = compJson.data;
              if (Array.isArray(companies) && companies.length > 0) {
                store.setCurrentCompany(companies[0].id);
              } else {
                // No companies — redirect to onboarding
                window.location.href = "/onboarding";
                return;
              }
            } else if (compRes.status === 403) {
              window.location.href = NO_COMPANY_ACCESS_LOGIN_URL;
              return;
            }
          } catch {
            // Non-critical — company can be selected later
          }
        }
      } catch {
        store.reset();
        try {
          await fetch(`${getBaseUrl()}/platform/auth/logout`, {
            method: "POST",
            credentials: "include",
          });
        } catch {
          /* ignore */
        }
        window.location.href = "/login";
        return;
      } finally {
        store.setLoading(false);
      }
    }

    checkAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return <MainLoadingSkeleton />;
  }

  if (!isAuthenticated) {
    // Not authenticated after loading complete — redirect to login
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
    return <MainLoadingSkeleton />;
  }

  return (
    <PlatformWebSocketProvider>
      {children}
      <CommandPalette />
    </PlatformWebSocketProvider>
  );
}
